import { Box, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { TextFilter } from "../../components/common/TextFilter";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useOffers } from "../../services/offersService";
import TableService from "../../services/tableService";
import { OfferCreateDialog } from "./OfferDialogs/OfferCreateDialog";
import { OFFERS_TABLE_NAME, useOfferTableFieldsConfig } from "./OffersPageTableConfig";
import { OfferPreviewButton } from "./OfferPreviewButton";

export function OffersPage(props) {
    const { pageName } = props;
    const { t } = useTranslation();
    const { openDialog } = useDialogBetter();
    const [hasEditPermission] = [true];

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);

    const handleOpenOfferCreateDialog = () => {
        openDialog({
            Component: OfferCreateDialog,
            componentProps: {},
        });
    };

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: offersData, isLoading: isOffersLoading, refetch } = useOffers(searchParams);

    useEffect(() => {
        if (offersData?.results) {
            setDataLocal(offersData.results);
            setRecordsCount(offersData.count);
        }
    }, [offersData, dataPage, dataPageSize]);

    const offersTableFieldsConfig = useOfferTableFieldsConfig(refetch);

    const offerDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            offersTableFieldsConfig,
            hiddenColumnsForTables?.[OFFERS_TABLE_NAME],
            columnsOrdersForTables?.[OFFERS_TABLE_NAME],
        );
    }, [
        hiddenColumnsForTables?.[OFFERS_TABLE_NAME],
        columnsOrdersForTables?.[OFFERS_TABLE_NAME],
        offersTableFieldsConfig,
    ]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setTableLoading(true);
            await refetch();
            setTableLoading(false);
        };
        fetchData();
    }, [searchParams]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                offersTableFieldsConfig,
                dataLocal,
                hiddenColumnsForTables?.[OFFERS_TABLE_NAME]
                    ? hiddenColumnsForTables?.[OFFERS_TABLE_NAME]
                    : [],
                columnsOrdersForTables?.[OFFERS_TABLE_NAME]
                    ? columnsOrdersForTables?.[OFFERS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            offersTableFieldsConfig,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    return (
        <DefaultPageWrapper tagTitle={t("headers.offers")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "start",
                            width: "40%",
                            marginLeft: "50px",
                        }}
                    >
                        <TextFilter
                            filterKey={"customer"}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            resetPageNumber={resetPageNumber}
                            withPagination
                            placeholder={t("clients.filter.masterFilterPlaceholder") ?? undefined}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={{
                            name: OFFERS_TABLE_NAME,
                            config: offersTableFieldsConfig,
                        }}
                        filteringData={() => {}}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"offer"}
                        style={{ marginBlock: "5px" }}
                        showCreateButton={hasEditPermission}
                        onClickCreateItem={handleOpenOfferCreateDialog}
                        mdGridButton={hasEditPermission ? 2.0 : 3.25}
                        extraButtonList={[<OfferPreviewButton/>]}
                        showImportList={false}
                        showRemoveLeadButton={false}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={isOffersLoading && !dataLocal.length}>
                        <FilterTable
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.orderColumns(
                                TableService.hideHiddenColumns(
                                    offersTableFieldsConfig,
                                    hiddenColumnsForTables?.[OFFERS_TABLE_NAME]
                                        ? hiddenColumnsForTables?.[OFFERS_TABLE_NAME]
                                        : [],
                                ),
                                columnsOrdersForTables?.[OFFERS_TABLE_NAME]
                                    ? columnsOrdersForTables?.[OFFERS_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"offers/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={offerDataExportHeaders}
                            exportToFileFileName={`${t("offers.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={false}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                            isLoading={tableLoading}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
